import { memo, useState } from "react";
import {
  Alert,
  Box,
  IconButton,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LoadingComponent from "../components/LoadingComponent";
import TableLoader from "../components/TableLoader";
import AdminCourierActions from "../components/SettingCourierActions.jsx";
import EditSettingCourierModal from "../components/modals/SettingCourier/EditSettingCourierModal.jsx";
import { useAdminCouriers } from "../hooks/useAdminCouriers";

const AdminCouriers = () => {
  const {
    adminCouriers,
    templates,
    loading,
    error,
    pagination,
    adminCouriersCount,
    handleChangePage,
    handleChangeRowsPerPage,
    createCourier,
    updateCourier,
    deleteCourier,
  } = useAdminCouriers();

  const [selectedCourier, setSelectedCourier] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenEditModal = (courier) => {
    setSelectedCourier(courier);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCourier(null);
  };

  const handleTablePageChange = (event, newPage) => {
    handleChangePage(newPage + 1);
  };

  const handleRowsPerPageChange = (event) => {
    handleChangeRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleUpdateCourier = async (data) => {
    try {
      await updateCourier(selectedCourier.id, data);
      alert("Credenciales del proveedor actualizadas con éxito");
      handleCloseModal();
    } catch (error) {
      console.error(`Error al realizar el request: ${error}`);
    } finally {
      handleCloseModal();
    }
  };

  const handleDelete = async (courierId) => {
    if (window.confirm("¿Estás seguro que quieres eliminar las credenciales de este proveedor?")) {
      await deleteCourier(courierId);
    }
  };

  return (
    <>
      {loading ? <LoadingComponent /> : null}
      {error ? (
        <Alert sx={{ borderRadius: "0px !important" }} severity="error">
          {error}
        </Alert>
      ) : null}

      <Grid item xs={12} className="mg-t-20">
        <h2>Mis Proveedores Externos</h2>
      </Grid>

      <AdminCourierActions
        loading={loading}
        error={error}
        createCourier={createCourier}
        templates={templates}
      />

      <Grid container>
        <Grid container className="custom-table-container">
          <Grid item xs={12} className="flex-center mg-t-0">
            <>
              <TableContainer>
                {loading ? (
                  <TableLoader />
                ) : (
                  <>
                    <Table
                      aria-label="tabla de credenciales couriers"
                      className="fixed-table"
                    >
                      <TableHead sx={{ backgroundColor: "#fff" }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Proveedor
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Keys Configuradas
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            Última Actualización
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }} align="right">
                            Acciones
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {adminCouriers.map((courier) => (
                          <TableRow
                            key={courier.id}
                            hover
                            sx={{
                              "& > *": { borderBottom: "unset" },
                              backgroundColor: "#fff",
                            }}
                          >
                            <TableCell component="th" scope="row">
                              {courier.name}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {Object.keys(courier.configuration || {}).length} keys activas
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {new Date(courier.updated_at).toLocaleDateString()}
                            </TableCell>
                            <TableCell component="th" scope="row" align="right">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditModal(courier)}
                                title="Editar Credenciales"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(courier.id)}
                                title="Eliminar Proveedor"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}

                {!loading && adminCouriers.length === 0 && (
                  <Box sx={{ p: 4, textAlign: "center" }}>
                    <Typography color="text.secondary">
                      No se encontraron credenciales de proveedores configuradas.
                    </Typography>
                  </Box>
                )}

                <TablePagination
                  rowsPerPageOptions={[20, 40, 60, 80, 100]}
                  component="div"
                  count={adminCouriersCount}
                  rowsPerPage={pagination.rowsPerPage}
                  page={pagination.page - 1}
                  onPageChange={handleTablePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              </TableContainer>

              <EditSettingCourierModal
                open={isModalOpen}
                onClose={handleCloseModal}
                onConfirm={handleUpdateCourier}
                initialData={selectedCourier}
                templates={templates}
              />
            </>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default memo(AdminCouriers);
